import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Zap, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import TurnstileWidget, { isTurnstileEnabled } from '../../components/auth/TurnstileWidget';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const [branding, setBranding] = useState({ hub_name: 'SpillConsult Hub', hub_logo: null });

  useEffect(() => {
    fetch(`${BACKEND}/api/hub/public-branding`)
      .then(r => r.json())
      .then(data => setBranding(data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isTurnstileEnabled && !turnstileToken) {
      toast.error('Please complete the verification challenge');
      return;
    }
    setLoading(true);
    try {
      await authApi.forgotPassword(email, turnstileToken);
      setSubmitted(true);
      toast.success('Reset link sent if email exists');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#06070a] relative overflow-hidden" data-testid="forgot-password-page">
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/[0.06] rounded-full blur-[100px]" />

      <div className="relative z-10 w-full max-w-md px-4">
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="mx-auto w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/20 overflow-hidden">
                {branding.hub_logo ? (
                  <img src={`${BACKEND}${branding.hub_logo}`} alt={branding.hub_name} className="w-full h-full object-contain p-1" />
                ) : (
                  <Zap className="w-7 h-7 text-white" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                {submitted ? 'Check Your Email' : 'Forgot Password'}
              </h1>
              <p className="text-sm text-white/30 mt-1">
                {submitted
                  ? 'We sent a password reset link to your email'
                  : 'Enter your email to receive a reset link'}
              </p>
            </div>

            {submitted ? (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                </div>
                <div className="text-center space-y-2">
                  <p className="text-sm text-white/50">
                    If an account exists for <strong className="text-white/70">{email}</strong>, you will receive a password reset link shortly.
                  </p>
                  <p className="text-xs text-white/30">The link will expire in 1 hour.</p>
                </div>
                <div className="space-y-2">
                  <Button variant="outline"
                    className="w-full bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10"
                    onClick={() => { setSubmitted(false); setEmail(''); }}
                    data-testid="try-another-email-btn">
                    Try another email
                  </Button>
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full text-white/40 hover:text-white" data-testid="back-to-login-btn">
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-white/40">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                    <Input
                      type="email"
                      placeholder="name@example.com"
                      className="pl-10 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-blue-500/50 focus:ring-blue-500/20 h-11"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <div className="flex justify-center">
                  <TurnstileWidget ref={turnstileRef} action="forgot_password" onVerify={setTurnstileToken} theme="dark" />
                </div>
                <Button
                  type="submit"
                  disabled={loading || (isTurnstileEnabled && !turnstileToken)}
                  className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium shadow-lg shadow-blue-500/20 transition-all"
                  data-testid="send-reset-link-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full text-white/40 hover:text-white" type="button" data-testid="cancel-forgot-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to login
                  </Button>
                </Link>
              </form>
            )}
          </div>
          <div className="px-8 py-4 border-t border-white/[0.04] text-center">
            <p className="text-[11px] text-white/20">SpillConsult Hub</p>
          </div>
        </div>
      </div>
    </div>
  );
}
